import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import DateFnsUtils from '@date-io/date-fns';
import { groupBy } from 'lodash';
import gradeIcon from '@/assets/grade.svg';
import ageIcon from '@/assets/age.svg';
import PublicIcon from '@mui/icons-material/Public';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { AccessibleDivButton, Chip, StickyHeader } from '@/components';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { FlyerStatus } from '@/types/flyer';
import { CallToAction, Flyer, FlyerDistrict, GetDistrictResponse } from '@/graphql/types';
import { useIsMobile } from '@/hooks';
import { CTAMapper } from '@/pages/FlyerDetails/CtaMapper';
import styles from './FlyerDetailsInfo.styles';
import {
  BodyMedium,
  BodyMediumMidEmphasis,
  BodyMediumMidEmphasisUnderline,
  BodySmall,
  BodySmallMidEmphasis,
  BodySmallMidEmphasisUnderline,
  TitleSmallEmphasized,
} from '@/design-system';
import colors from '@/theme/colors';
import { AidaStatus, EXPANDED_COMMUNITY_FLYER_DISCLAIMER, FlyerType } from '@/config/constants';
import { useNavigate } from 'react-router-dom';
import { FeatureLinkCards } from './FeatureLinkCards';
import { useGetFeaturedLinks } from '../InteractiveFlyer/useGetFeaturedLinks';
import { useAnalytics } from '@/hooks';
import { TrackingEvents, TrackingStage } from '@/config/tracking';
import { LanguageDropDown } from '../AccessibleVersion/LanguageDropDown';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { AccessibleVersion } from '../AccessibleVersion';
import { cx } from '@emotion/css';

interface FlyerDetailsProps {
  flyer: Flyer;
  isCommunity: boolean;
  onShare: () => void;
  goToOrgProfile: () => void;
  downloadPDF: (url: string) => void;
  addToCalendar: () => void;
  openLink: ({
    origin,
    value,
    cta,
    featureId,
  }: {
    origin: string;
    value: string;
    cta: string;
    featureId?: string | null;
  }) => void;
  emailClick: (origin: string, email: string) => void;
  viewLocation: () => void;
  flyerTitle: string;
  district?: FlyerDistrict;
  isEmbedded: boolean;
}

export const FlyerDetailsInfo: React.FC<FlyerDetailsProps> = ({
  flyer,
  isCommunity,
  onShare,
  goToOrgProfile,
  downloadPDF,
  addToCalendar,
  openLink,
  emailClick,
  viewLocation,
  flyerTitle,
  district,
  isEmbedded,
}) => {
  const dateFns = new DateFnsUtils();
  const trackId = 'flyer_details';
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [showAccessibleVersion, setShowAccessibleVersion] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [stickyTop, setStickyTop] = useState(0);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const isSchoolFlyer = flyer.type === FlyerType.school;

  const communityFlyerDisclaimer = flyer.isExpandedCommunity
    ? EXPANDED_COMMUNITY_FLYER_DISCLAIMER
    : district?.communitySettings.flyerDisclaimer;

  const { aidaStatus } = district?.communitySettings || {
    aidaStatus: AidaStatus.off,
  };

  const {
    language: { code: userPreferenceLanguage },
    defaultLanguage,
  } = useContext(UserPreferencesContext);

  const userHasPreferenceLanguage = userPreferenceLanguage !== defaultLanguage;
  const TextVersionLabel = isMobile
    ? BodySmallMidEmphasisUnderline
    : BodyMediumMidEmphasisUnderline;

  useEffect(() => {
    if (userHasPreferenceLanguage) {
      setSelectedLanguage(userPreferenceLanguage);
    }
  }, [userHasPreferenceLanguage]);

  const secondaryCTAs = flyer.ctas.slice(1);
  const { featuredLinks } = useGetFeaturedLinks(secondaryCTAs, flyer.featuredLinkIds);

  const postedOnDate = useMemo(
    () => dateFns.formatByString(new Date(flyer.submittedAt), 'MMMM dd, yyyy'),
    [flyer]
  );

  useEffect(() => {
    const headerFixed = document.getElementById('headerFixed');
    if (headerFixed) {
      setStickyHeaderHeight(headerFixed.offsetHeight);
    }
  }, []);

  const onStickyTopChanged = (stickyTop: number) => {
    setStickyTop(stickyTop);
  };

  const Section = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.infoSection}>{children}</div>
  );

  const SectionInfo = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.infoContainer}>{children}</div>
  );

  const SectionIcon = ({ icon: Icon }: { icon: React.ElementType }) => (
    <div className={styles.iconContainer}>
      <Icon />
    </div>
  );

  const onClickAccessibleVersion = () => {
    setShowAccessibleVersion(true);
    trackEvent({
      event: TrackingEvents.viewAccessibleFlyer,
      event_detail: TrackingEvents.viewAccessibleFlyer,
      stage: TrackingStage.consideration,
    });
  };

  const onClickLanguage = () => {
    setShowAccessibleVersion(true);
    trackEvent({
      event: TrackingEvents.viewAccessibleFlyer,
      event_detail: TrackingEvents.viewAccessibleFlyer,
      stage: TrackingStage.consideration,
    });
  };

  const onSelectLanguage = (newLanguage: string) => {
    setSelectedLanguage(newLanguage);
    setTimeout(() => {
      onClickLanguage();
    }, 500);
  };

  const onToggleAccessibleVersion = (state: boolean) => {
    setShowAccessibleVersion(state);
    if (!state && !userHasPreferenceLanguage) {
      setSelectedLanguage('');
    }
  };

  const SectionTextLink = ({
    onClick,
    dataTestId,
    text,
    tabIndex,
  }: {
    text: string;
    onClick?: () => void;
    dataTestId: string;
    tabIndex?: number;
  }) => (
    <AccessibleDivButton onClick={onClick} tabIndex={tabIndex}>
      <BodyMediumMidEmphasis
        className={styles.textLink}
        color={colors.jungle}
        data-testid={dataTestId}
      >
        {text}
      </BodyMediumMidEmphasis>
    </AccessibleDivButton>
  );

  const gradesAgeRange = useMemo(() => {
    const type = isSchoolFlyer ? 'grade' : 'years old';
    if (
      flyer?.minTargetGradeOrAge &&
      flyer?.maxTargetGradeOrAge &&
      flyer?.minTargetGradeOrAge === flyer?.maxTargetGradeOrAge
    ) {
      return `${flyer?.minTargetGradeOrAge} ${type}`;
    }

    return `${flyer?.minTargetGradeOrAge} - ${flyer?.maxTargetGradeOrAge} ${type}`;
  }, [flyer]);

  function removeUrlPrefix(url) {
    return url.replace(/^(https?:\/\/)?(www\.)?/, '');
  }

  const featureLinkList = useCallback(() => {
    if (featuredLinks.length === 0) return null;
    const ctasByType = groupBy(featuredLinks, cta => {
      return cta.type;
    });

    const sortedKeys = Object.keys(ctasByType).sort((a, b) => {
      const orderA = CTAMapper[a].order;
      const orderB = CTAMapper[b].order;
      return orderA - orderB;
    });

    return sortedKeys.map(key => (
      <Section key={key}>
        <SectionIcon icon={CTAMapper[key].icon} />
        <SectionInfo>
          {ctasByType[key].map((cta, index) => {
            const value = removeUrlPrefix(cta.value);
            return (
              <SectionTextLink
                key={`${cta.type}_${index}`}
                text={value}
                onClick={() =>
                  openLink({
                    origin: `${trackId}:cta:${cta.type}:${cta.value}`,
                    value: cta.value,
                    cta: key,
                    featureId: cta.id,
                  })
                }
                dataTestId={cta.type}
              />
            );
          })}
        </SectionInfo>
      </Section>
    ));
  }, [featuredLinks]);

  const ByOrgName = () => {
    if (!isCommunity || !flyer.org?.name) return null;

    return (
      <div className={styles.postedBy}>
        <BodySmallMidEmphasis nonTranslate>{`by ${flyer.org?.name}`}</BodySmallMidEmphasis>
      </div>
    );
    /* return (
      <div className={styles.postedBy} onClick={() => goToOrgProfile()}>
        <BodySmallMidEmphLasis>by&nbsp;</BodySmallMidEmphasis>
        <BodySmallMidEmphasisUnderline nonTranslate color={colors.jungle} className={styles.orgName}>
          {flyer.org?.name}
        </BodySmallMidEmphasisUnderline>
      </div>
    )*/
  };;

  return (
    <div className={styles.container}>
      {flyer.type === FlyerType.community && communityFlyerDisclaimer && (
        <div className={styles.activityDescription}>
          <BodySmall color={colors.charcoal}>{communityFlyerDisclaimer}</BodySmall>
        </div>
      )}
      {isEmbedded && (
        <div className={styles.embeddedFlyerInfo}>
          <BodyMedium color={colors.slate}>Posted {postedOnDate}</BodyMedium>
          <BodyMedium color={colors.slate}>Flyer ID {flyer.flyerId}</BodyMedium>
        </div>
      )}
      {flyer.wcagHtml && aidaStatus !== AidaStatus.off && (
        <div className={styles.accessibleVersion}>
          <AccessibleDivButton onClick={onClickAccessibleVersion} aria-haspopup="dialog">
            <TextVersionLabel color={colors.jungle}>Text Version</TextVersionLabel>
          </AccessibleDivButton>
          <LanguageDropDown
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={onSelectLanguage}
            onClick={userHasPreferenceLanguage ? onClickLanguage : undefined}
          />
        </div>
      )}
      <div
        className={cx(
          styles.flyerDetailsInfo,
          aidaStatus === AidaStatus.off && styles.aidaOffSpacing
        )}
      >
        <StickyHeader
          stickyHeaderHeight={stickyHeaderHeight}
          onStickyTopChanged={onStickyTopChanged}
        />
        {/* {renderChip()} */}
        {isEmbedded ? (
          <ByOrgName />
        ) : (
          <div className={styles.titleContainer}>
            <TitleSmallEmphasized
              className={styles.title}
              style={{ top: `${stickyTop}px` }}
              nonTranslate
              data-testid="flyerTitle"
              aria-label={`${isSchoolFlyer ? 'School' : 'Community'} Flyer ${flyerTitle}`}
              as="h1"
            >
              {flyerTitle}
            </TitleSmallEmphasized>
            <ByOrgName />
            <BodySmallMidEmphasis color={colors.slate}>Posted {postedOnDate}</BodySmallMidEmphasis>
          </div>
        )}
        <FeatureLinkCards trackId={trackId} ctas={featuredLinks} openLink={openLink} />
        {/* <div className={styles.metricts}>
            color={colors.slate}
            className={cx(isExpired && styles.textDisabled)}
            data-testid="viewsPerDay"
            >
              {flyer.viewsPerDay} viewed &nbsp;{'•'}&nbsp;
          </BodySmallMidEmphasis>
          <BodySmallMidEmphasis
            color={colors.slate}
            className={cx(isExpired && styles.textDisabled)}
            data-testid="savedAmount"
            >
              {flyer.savedAmount} saved
          </BodySmallMidEmphasis>
        </div> */}
        <Section>
          <BodyMedium className={styles.iconContainer}>
            <img src={isSchoolFlyer ? gradeIcon : ageIcon} alt="" height="22px" width="22px" />
            {gradesAgeRange}
          </BodyMedium>
        </Section>
        {/* <Section>
          <SectionIcon
            icon={CalendarTodayOutlinedIcon}
            isExpired={isExpired}
          />
          <SectionInfo>
            <BodyMedium
              className={cx(isExpired && styles.textDisabled)}
              data-testid="eventDate"
            >
              {formatDate(flyer.startDate, flyer.endDate)}
            </BodyMedium>
            <BodyMedium
              className={cx(isExpired ? styles.iconDisabled : styles.textGray)}
              data-testid="eventDate"
            >
              {formatTime(flyer.startDate, flyer.endDate)}
            </BodyMedium>
            {!isExpired && (
              <SectionTextLink
                text="Add to calendar"
                onClick={addToCalendar}
                dataTestId="addToCalendarButton"
              />
            )}
          </SectionInfo>
        </Section> */}
        {flyer.mapUrl && (
          <Section>
            <SectionIcon icon={LocationOnOutlinedIcon} />

            <SectionInfo>
              <BodyMedium data-testid="eventAddress">{flyer.eventLocation}</BodyMedium>
              <SectionTextLink
                text="View location"
                onClick={() => viewLocation()}
                dataTestId="viewLocation"
              />
            </SectionInfo>
          </Section>
        )}
        {/*TODO: ENG-2249: keep the feature link list for A/B testing using Flaggr*/}
        {/* {featureLinkList()} */}
      </div>
      <AccessibleVersion
        isOpen={showAccessibleVersion}
        onToggle={onToggleAccessibleVersion}
        wcagHtml={flyer.wcagHtml}
        preSelectedLanguage={selectedLanguage}
        district={district}
        id={flyer.flyerId}
      />
    </div>
  );
};
